import { useState } from "react";

import { api, ApiError, type PlayerAccountOwnership } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";

/** De quién es la cuenta de un jugador, para SOPORTE (Opción A, Eduardo
 * 2026-09-30).
 *
 * La cuenta de un jugador solo la toca la organización que lo dio de alta. Un
 * jugador que soporte cargó suelto (o un registro antiguo) no es de ninguna
 * hasta que soporte lo asigna: aquí, eligiendo uno de los torneos donde juega.
 * Los organizadores de ese torneo pasan a ser los dueños de su cuenta. Solo lo
 * ve el superadministrador (el backend le manda `account_ownership` solo a él). */

interface Props {
  token: string;
  playerProfileId: number;
  playerName: string;
  ownership: PlayerAccountOwnership;
  /** Torneos donde juega el jugador (vigentes e historial), sin repetir. */
  tournaments: Array<{ id: number; name: string }>;
  /** Tras asignar o quitar: la ficha se vuelve a cargar. */
  onChanged: () => void;
}

export default function PlayerAccountOwnerPanel({ token, playerProfileId, playerName, ownership, tournaments, onChanged }: Props) {
  const { askConfirm } = useAbortableConfirm([playerProfileId]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const owners = ownership.owners.map((owner) => owner.full_name || `Organizador #${owner.user_id}`);
  const entry = ownership.entry_tournament;

  async function save(tournamentId: number | null, done: string) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.superadmin.setPlayerEntryTournament(token, playerProfileId, tournamentId);
      setMessage(done);
      setSelected("");
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo guardar." : "Error de red. Intenta otra vez.");
    } finally {
      setBusy(false);
    }
  }

  async function handleAssign() {
    const tournament = tournaments.find((item) => String(item.id) === selected);
    if (!tournament) return;
    const ok = await askConfirm(
      `¿Asignar la cuenta de ${playerName} a la organización del torneo «${tournament.name}»? Sus organizadores podrán ` +
        "generarle PIN, entrar como él y mandarle sus datos de acceso.",
      { acceptLabel: "Sí, asignar", dismissLabel: "No, regresar" },
    );
    if (!ok) return;
    await save(tournament.id, `Listo: la cuenta de ${playerName} ahora es de la organización del torneo «${tournament.name}».`);
  }

  async function handleClear() {
    const ok = await askConfirm(
      `¿Quitarle a ${playerName} su torneo de entrada? Los organizadores de «${entry?.name ?? "ese torneo"}» dejarán de ` +
        "poder tocar su cuenta (a menos que ellos lo hayan creado).",
      { acceptLabel: "Sí, quitar", tone: "danger", dismissLabel: "No, regresar" },
    );
    if (!ok) return;
    await save(null, `Listo: ${playerName} ya no tiene torneo de entrada.`);
  }

  return (
    // Solo los avisos fijos llevan data-no-toast: el resultado de «Asignar» o
    // «Quitar» (respuesta a un clic) sí sale también como toast.
    <div className="account-owner-panel">
      <p className="muted" role="note" data-no-toast>
        <strong>Organización dueña de la cuenta (solo lo ve soporte):</strong>{" "}
        {owners.length > 0
          ? `${owners.join(", ")}.${entry ? ` Entró por el torneo «${entry.name}».` : ""}`
          : `ninguna: hoy la cuenta de ${playerName} solo la puede tocar soporte.${
              entry ? ` Su torneo de entrada («${entry.name}») no tiene organizador con poder total.` : ""
            }`}
      </p>
      {tournaments.length > 0 ? (
        <div className="form">
          <label>
            Asignarla a la organización del torneo
            <select value={selected} onChange={(event) => setSelected(event.target.value)} disabled={busy}>
              <option value="">Elige un torneo…</option>
              {tournaments.map((tournament) => (
                <option key={tournament.id} value={tournament.id}>
                  {tournament.name}
                </option>
              ))}
            </select>
          </label>
          <div className="form__actions">
            <button type="button" className="btn btn--ghost btn--compact" onClick={() => void handleAssign()} disabled={busy || !selected}>
              {busy ? "Guardando…" : "Asignar"}
            </button>
            {entry ? (
              <button type="button" className="btn btn--ghost btn--compact btn--tap44" onClick={() => void handleClear()} disabled={busy}>
                Quitar torneo de entrada
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="muted" data-no-toast>
          Este jugador no está inscrito en ningún torneo: inscríbelo primero en el torneo de su organización.
        </p>
      )}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {message ? <p className="form-message">{message}</p> : null}
    </div>
  );
}
